import { Browser } from "@capacitor/browser";
import { isNativePlatform } from "@/lib/nativeVideoUpload";
import { publicOrigin } from "@/lib/publicOrigin";
import { isAndroid } from "@/lib/installPrompt";

// Fired by lib/deepLinks.js when a yalola://open/payment/... link re-enters the app.
export const PAYMENT_RETURN_EVENT = "yalola:payment-return";

// Origin used to build Stripe success/cancel URLs. On native the app is served
// from a local scheme (no real server), so Stripe's redirect must land on the
// public web origin instead of localhost.
export const checkoutOrigin = () => publicOrigin();

// Fields every checkout payload sends: the public origin + the native flag, so the
// backend tags Stripe's return URLs with app=1 and the public result page hands the
// user straight back to the installed app instead of stranding them on the website.
export const checkoutReturn = () => ({ origin_url: publicOrigin(), native_app: isNativePlatform() });

// Lien qui ré-ouvre l'app installée sur /payment/… depuis la page web de résultat Stripe.
// Android : intent explicite vers le paquet app.yalola portant l'URL https (App Link) — résolu même si le
// téléphone n'a pas vérifié yalola.app (Xiaomi…), sans schéma personnalisé. Pas d'URL de repli : si
// l'app ne s'ouvre pas, Chrome reste sur la page web de résultat (bouton + consigne), et l'app retrouve
// de toute façon le résultat à son retour au premier plan (openStripeCheckout / resumePendingCheckout).
// iOS : schéma personnalisé (un lien universel vers le domaine déjà affiché resterait dans Safari).
export const appHandoffUrl = (path) => {
  if (isAndroid()) {
    const u = new URL(`${publicOrigin()}${path}`);
    return `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;package=app.yalola;end`;
  }
  return `yalola://open${path}`;
};

// Checkout en attente (app mobile) : mémorisé le temps du passage dans le navigateur intégré pour que
// l'app retrouve le résultat même si elle a été relancée à froid entre-temps (Xiaomi ferme volontiers
// les apps en arrière-plan). Consommé par resumePendingCheckout() au démarrage.
const PENDING_KEY = "yalola.pendingCheckout";
const PENDING_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const rememberPending = (sessionId, extra = {}) => {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ sessionId, at: Date.now(), ...extra })); } catch { /* noop */ }
};
const clearPendingCheckout = () => { try { localStorage.removeItem(PENDING_KEY); } catch { /* noop */ } };
// → route interne à ouvrir (ou null) ; efface la mémoire dans tous les cas.
export const resumePendingCheckout = () => {
  let raw = null;
  try { raw = localStorage.getItem(PENDING_KEY); } catch { /* noop */ }
  if (!raw) return null;
  clearPendingCheckout();
  try {
    const p = JSON.parse(raw);
    if (!p?.sessionId || Date.now() - (p.at || 0) > PENDING_MAX_AGE_MS) return null;
    return `/payment/success?session_id=${encodeURIComponent(p.sessionId)}`;
  } catch {
    return null;
  }
};

// Ouvre un Stripe Checkout. Sur l'app mobile : navigateur intégré (Custom Tab Android /
// SFSafariViewController iOS). On revient dans l'app par l'un de ces chemins — le premier gagne :
//   1. le lien de retour tiré par la page web de résultat (App Link / yalola://) → événement PAYMENT_RETURN_EVENT ;
//   2. l'utilisateur ferme le navigateur intégré (browserFinished) ;
//   3. l'app repasse au premier plan par n'importe quel autre moyen (appStateChange, Android).
// Dans tous les cas `onReturn(sessionId, { cancelled })` mène à l'écran de résultat, qui relit l'état
// RÉEL du paiement chez Stripe (payé / annulé) — jamais de confirmation sans paiement.
// Sur le web : simple redirection (inchangé).
export const openStripeCheckout = async ({ url, sessionId, onReturn }) => {
  if (!isNativePlatform()) {
    window.location.href = url;
    return;
  }
  rememberPending(sessionId);
  let done = false;
  let handle = null;
  let stateHandle = null;
  let wentBackground = false;
  function finish(result) {
    if (done) return;
    done = true;
    clearPendingCheckout();
    window.removeEventListener(PAYMENT_RETURN_EVENT, onDeepLink);
    try { handle?.remove(); } catch { /* noop */ }
    try { stateHandle?.remove(); } catch { /* noop */ }
    onReturn?.(sessionId, result);
  }
  // iOS : Browser.close() ferme la feuille SANS émettre browserFinished → le lien de retour conclut
  // lui-même. Android : close() est sans effet (l'intent singleTask a déjà fermé le Custom Tab).
  function onDeepLink(e) {
    if (e?.detail) e.detail.handled = true;
    Browser.close().catch(() => {});
    finish({ cancelled: !!e?.detail?.cancelled });
  }
  window.addEventListener(PAYMENT_RETURN_EVENT, onDeepLink);
  handle = await Browser.addListener("browserFinished", () => finish({ cancelled: false }));
  try {
    // Android : le Custom Tab met l'app en arrière-plan ; son retour au premier plan = retour du payeur.
    // On laisse 600 ms au lien de retour (plus précis : succès / annulation) pour arriver d'abord.
    const { App } = await import("@capacitor/app");
    stateHandle = await App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) { wentBackground = true; return; }
      if (wentBackground) setTimeout(() => finish({ cancelled: false }), 600);
    });
  } catch { /* plugin absent : chemins 1 et 2 suffisent */ }
  await Browser.open({ url, toolbarColor: "#0A0D0C" });
};
